import { buildShipperServiceKey, parseShipperServiceKey } from 'skladon-utils';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { GridFilterType, TableRowType } from 'src/types/table';

export const UNKNOWN_SHIPPER_CODE = 'UNKNOWN';

/**
 * Service code options for filters/cells where value = "<SHIPPER_CODE>||<VALUE>".
 * Labels shared by more than one service code get the shipper appended, e.g. "Exchange service (GLS)".
 */
export const buildShipperServiceOptions = (
    serviceCodes: ConstantsCodeType[],
    shipperCodes: ConstantsCodeType[]
): ConstantsCodeType[] => {
    const labelCounts = new Map<string, number>();
    serviceCodes.forEach((serviceCode) => {
        labelCounts.set(serviceCode.label, (labelCounts.get(serviceCode.label) || 0) + 1);
    });

    return serviceCodes
        .filter((serviceCode) => !!serviceCode.shipperCode)
        .map((serviceCode): ConstantsCodeType => {
            const shipperCode = serviceCode.shipperCode as string;
            let label = serviceCode.label;
            if ((labelCounts.get(serviceCode.label) || 0) > 1) {
                const shipperLabel =
                    shipperCodes.find((shipper) => shipper.value === shipperCode)?.label || shipperCode;
                label = `${serviceCode.label} (${shipperLabel})`;
            }
            return { ...serviceCode, label, value: buildShipperServiceKey(shipperCode, String(serviceCode.value)) };
        })
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
};

/** Key of the row's service code in options built by buildShipperServiceOptions. */
export const getRowShipperServiceKey = (row: TableRowType, shipperColumn: string, serviceColumn: string): string => {
    const shipperCode = row[shipperColumn];
    const serviceCode = row[serviceColumn];
    if (!shipperCode || !serviceCode) {
        return '';
    }
    return buildShipperServiceKey(String(shipperCode), String(serviceCode));
};

/**
 * Replaces filter on `sourceColumn` (composite keys) with the virtual composite column understood by the grid API.
 * Legacy values without shipper (old sessionStorage) are expanded to all shipper/service pairs with such VALUE.
 */
export const mapShipperServiceFilters = (
    filters: GridFilterType[],
    sourceColumn: string,
    targetColumn: string,
    serviceCodes: ConstantsCodeType[]
): GridFilterType[] =>
    filters.reduce<GridFilterType[]>((result, filter) => {
        if (filter.column !== sourceColumn) {
            result.push(filter);
            return result;
        }
        const rawValues = Array.isArray(filter.value) ? filter.value : [filter.value];
        const keys: string[] = [];
        rawValues.forEach((rawValue) => {
            if (parseShipperServiceKey(rawValue)) {
                keys.push(rawValue);
                return;
            }
            const matches = serviceCodes.filter(
                (serviceCode) => !!serviceCode.shipperCode && String(serviceCode.value) === String(rawValue)
            );
            if (matches.length) {
                matches.forEach((serviceCode) =>
                    keys.push(buildShipperServiceKey(serviceCode.shipperCode as string, String(serviceCode.value)))
                );
            } else if (rawValue) {
                // unknown legacy value - matches nothing (backend rejects empty/invalid composite values)
                keys.push(buildShipperServiceKey(UNKNOWN_SHIPPER_CODE, String(rawValue)));
            }
        });
        const uniqueKeys = keys.filter((key, index) => keys.indexOf(key) === index);
        if (uniqueKeys.length) {
            result.push({ column: targetColumn, operator: 'in', value: uniqueKeys });
        }
        return result;
    }, []);
